/**
 * Does a web address answer yet? True once a request to it gets any HTTP
 * response at all.
 *
 * A project is "live" on the server the moment its first deploy lands, but a
 * brand-new address can go on refusing HTTPS for a few minutes after that
 * while its certificate is issued (measured on staging 2026-11-02: about
 * three minutes). Saying "your app is live" in that gap sends somebody to a
 * connection error. So the first build screen asks the address itself.
 *
 * `mode: 'no-cors'` because the app is another origin with no reason to allow
 * this one: the response comes back opaque, which is all that is needed, since
 * a fetch only rejects when nothing answered (no certificate, no connection).
 * Polls every few seconds while `enabled`, and stops for good once it answers.
 */

import { useEffect, useState } from 'react';

const POLL_MS = 5000;

export function useAddressAnswers(url: string | null | undefined, enabled: boolean): boolean {
  const [answers, setAnswers] = useState(false);

  useEffect(() => {
    if (!enabled || !url || answers) return;
    let stale = false;
    let timer: number | undefined;

    async function probe() {
      try {
        await fetch(url!, { mode: 'no-cors', cache: 'no-store', credentials: 'omit' });
        if (!stale) setAnswers(true);
      } catch {
        if (!stale) timer = window.setTimeout(probe, POLL_MS);
      }
    }
    void probe();

    return () => {
      stale = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [url, enabled, answers]);

  return answers;
}
